import { Fragment, type UIEvent } from "react";
import { Loader2 } from "lucide-react";
import type { LockupDrillThroughRow } from "@/analytics/lockup.api";
import { formatCurrency, formatText, getTableMinWidth } from "@/lib/drillThroughTableNormalizers";
import type { AgeBand } from "@/types/drillThroughTable";
import { getAriaSort, SortableColumnHeader } from "@/components/SortableColumnHeader";
import type { SortDir, SortState } from "@/lib/tableSort";

type IdentityKey = "assignment_manager" | "client_name" | "assignment_name";

export type LockupIdentityColumn = {
  key: IdentityKey;
  label: string;
  sortKey?: string;
};

type LockupDrillThroughDataTableProps = {
  title: string;
  rows: LockupDrillThroughRow[];
  ageBands: AgeBand[];
  identityColumns: LockupIdentityColumn[];
  loading?: boolean;
  loadingMessage?: string;
  refetching?: boolean;
  error?: string | null;
  totalRows?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  sort?: SortState;
  onSort?: (key: string, direction: SortDir | null) => void;
};

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const AGE_BAND_CELL_COLORS: Record<string, string> = {
  age_0_30: "bg-[#2f8f9d]/75 text-slate-950",
  age_31_60: "bg-[#78b760]/75 text-slate-950",
  age_61_90: "bg-[#d7bd45]/80 text-slate-950",
  age_91_120: "bg-[#d99355]/75 text-slate-950",
  age_120_180: "bg-[#cf777f]/75 text-slate-950",
  age_180_plus: "bg-[#b84f55]/80 text-white",
};

const ROW_HEIGHT_PX = 40;
const PREFETCH_ROWS_FROM_BOTTOM = 10;

const BREAKDOWN_ROWS: Array<{ label: string; key: "lockup" | "wip" | "aged_debt" }> = [
  { label: "WIP", key: "wip" },
  { label: "Debt", key: "aged_debt" },
  { label: "Lockup", key: "lockup" },
];

export function LockupDrillThroughDataTable({
  title,
  rows,
  ageBands,
  identityColumns,
  loading = false,
  loadingMessage,
  refetching = false,
  error = null,
  totalRows,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  sort,
  onSort,
}: LockupDrillThroughDataTableProps) {
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const element = event.currentTarget;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom <= PREFETCH_ROWS_FROM_BOTTOM * ROW_HEIGHT_PX) onLoadMore();
  };

  const valueColumns: Array<AgeBand & { sortKey?: string } | { key: "total"; label: string; sortKey?: string }> = [
    ...ageBands.map((band) => ({ ...band, sortKey: band.key })),
    { key: "total", label: "Total", sortKey: "total" },
  ];
  const identityWidth = "17%";
  const typeWidth = "8%";
  const valueWidth = identityColumns.length === 1 ? "10.71%" : "8.28%";
  const columnCount = identityColumns.length + 1 + valueColumns.length;

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label={title}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold text-card-foreground">{title}</h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          {refetching ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
          {loading ? "Loading" : totalRows !== undefined ? `${rows.length} / ${totalRows} rows` : `${rows.length} rows`}
        </span>
      </div>

      <div
        className={cx(
          "relative max-h-[calc(64vh-5rem)] min-h-[340px] overflow-auto",
          refetching && "opacity-60 transition-opacity",
        )}
        onScroll={handleScroll}
      >
        {loading && loadingMessage ? (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-card/60">
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingMessage}
            </div>
          </div>
        ) : null}

        <table
          className="w-full table-fixed border-separate border-spacing-0 text-xs"
          style={{ minWidth: getTableMinWidth(identityColumns.length + 1, valueColumns.length) }}
        >
          <colgroup>
            {identityColumns.map((column) => <col key={column.key} style={{ width: identityWidth }} />)}
            <col style={{ width: typeWidth }} />
            {valueColumns.map((column) => <col key={column.key} style={{ width: valueWidth }} />)}
          </colgroup>
          <thead>
            <tr>
              {identityColumns.map((column, index) => (
                <th
                  key={column.key}
                  className={cx(
                    "sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-left font-bold leading-tight text-accent-foreground",
                    index === 0 && "left-0 z-30",
                  )}
                  aria-sort={getAriaSort(column.sortKey, sort)}
                >
                  <SortableColumnHeader label={column.label} sortKey={column.sortKey} sortType="text" sort={sort} onSort={onSort} />
                </th>
              ))}
              <th className="sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-left font-bold leading-tight text-accent-foreground">
                Type
              </th>
              {valueColumns.map((column) => (
                <th
                  key={column.key}
                  className="sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-center font-bold leading-tight text-accent-foreground"
                  aria-sort={getAriaSort(column.sortKey, sort)}
                >
                  <SortableColumnHeader label={column.label} sortKey={column.sortKey} sort={sort} onSort={onSort} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRows columnCount={columnCount} />
            ) : rows.length === 0 && error ? (
              <tr>
                <td className="h-44 bg-card text-center text-destructive" colSpan={columnCount}>
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="h-44 bg-card text-center text-muted-foreground" colSpan={columnCount}>
                  No records found
                </td>
              </tr>
            ) : (
              <BreakdownRows rows={rows} identityColumns={identityColumns} valueColumns={valueColumns} />
            )}
            {!loading && loadingMore ? (
              <tr>
                <td className="border-b bg-card px-3 py-2.5 text-center text-muted-foreground" colSpan={columnCount}>
                  <span className="inline-flex items-center gap-2 text-xs">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading more...
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BreakdownRows({
  rows,
  identityColumns,
  valueColumns,
}: {
  rows: LockupDrillThroughRow[];
  identityColumns: LockupIdentityColumn[];
  valueColumns: Array<AgeBand | { key: "total"; label: string }>;
}) {
  return (
    <>
      {rows.map((row, rowIndex) => (
        <Fragment key={getRowKey(row, rowIndex)}>
          {BREAKDOWN_ROWS.map((breakdown, breakdownIndex) => {
            const values = row[breakdown.key];
            const isLockup = breakdown.key === "lockup";
            return (
              <tr key={breakdown.key} className={cx("hover:bg-secondary/50", rowIndex % 2 === 1 && "bg-muted/35", isLockup && "bg-primary/5")}>
                {breakdownIndex === 0
                  ? identityColumns.map((column, columnIndex) => (
                    <td
                      key={column.key}
                      rowSpan={BREAKDOWN_ROWS.length}
                      className={cx(
                        "border-b border-r px-3 py-2.5 align-top font-semibold leading-snug text-foreground",
                        columnIndex === 0 && "sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]",
                        columnIndex === 0 && rowIndex % 2 === 1 && "bg-muted",
                      )}
                    >
                      {formatText(row[column.key])}
                    </td>
                  ))
                  : null}
                <td className={cx("border-b border-r px-3 py-2.5 font-medium text-foreground", isLockup && "font-bold")}>
                  {breakdown.label}
                </td>
                {valueColumns.map((column) => {
                  const value = values?.[column.key];
                  return (
                    <td
                      key={column.key}
                      className={cx(
                        "border-b border-r px-3 py-2.5 text-center tabular-nums text-foreground",
                        column.key === "total" && "font-bold",
                        AGE_BAND_CELL_COLORS[column.key],
                      )}
                    >
                      {formatCurrency(value, 0)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}

function LoadingRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <tr key={rowIndex} className="even:bg-muted/35">
          {Array.from({ length: columnCount }).map((__, columnIndex) => (
            <td
              key={columnIndex}
              className={cx(
                "border-b border-r px-3 py-2.5",
                columnIndex === 0 && "sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]",
                columnIndex === 0 && rowIndex % 2 === 1 && "bg-muted",
              )}
            >
              <div className={cx("h-4 animate-pulse rounded bg-muted", columnIndex < 2 ? "w-24" : "mx-auto w-20")} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function getRowKey(row: LockupDrillThroughRow, rowIndex: number): string {
  return [row.assignment_manager, row.client_code, row.assignment_code, rowIndex].filter(Boolean).join("-");
}
